import React, { useEffect, useState, useRef } from 'react';
import AuthService from '../../services/AuthService';
import LeadStatusService from '../../services/LeadStatusService';
import { apiErrorMessage } from '../../../../shared/utils/apiErrorMessage';

const PRESET_COLORS = [
  '#5e72e4', '#11cdef', '#2dce89', '#fb6340', '#f5365c',
  '#ffd600', '#8965e0', '#f3a4b5', '#adb5bd', '#344767',
];

const defaultForm = { leadStatusName: '', color: '#5e72e4', sortOrder: 0 };

export default function PipelineStageManager() {
  const clientId = AuthService.getLoggedInClientId();
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await LeadStatusService.getPipelineStagesByClientId(clientId);
      setStages(data);
    } catch (e) {
      setError('Failed to load pipeline stages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (showForm && inputRef.current) inputRef.current.focus(); }, [showForm]);

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm);
    setError('');
    setShowForm(true);
  };

  const openEdit = (stage) => {
    setEditingId(stage.id);
    setForm({ leadStatusName: stage.leadStatusName, color: stage.color || '#5e72e4', sortOrder: stage.sortOrder || 0 });
    setError('');
    setShowForm(true);
  };

  const cancel = () => { setShowForm(false); setEditingId(null); setError(''); };

  const handleSave = async () => {
    if (!form.leadStatusName.trim()) { setError('Stage name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await LeadStatusService.updatePipelineStage(editingId, { ...form });
      } else {
        await LeadStatusService.createPipelineStage({ ...form, clientId });
      }
      setShowForm(false);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to save stage.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this custom stage? Global stages cannot be deleted.')) return;
    setDeletingId(id);
    try {
      await LeadStatusService.deletePipelineStage(id);
      await load();
    } catch (e) {
      alert(apiErrorMessage(e, 'Cannot delete this stage (it may be a global stage).'));
    } finally {
      setDeletingId(null);
    }
  };

  /* ── Styles ── */
  const card = {
    background: '#fff', borderRadius: '18px',
    boxShadow: '0 2px 24px rgba(52,71,103,0.09)',
    overflow: 'hidden', maxWidth: '780px', margin: '0 auto',
  };
  const hdr = {
    background: 'linear-gradient(135deg,#344767 0%,#21243d 100%)',
    padding: '24px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
  };
  const addBtn = {
    background: 'linear-gradient(135deg,#5e72e4,#825ee4)', border: 'none',
    borderRadius: '10px', padding: '9px 20px', color: '#fff',
    fontSize: '13px', fontWeight: '700', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '6px',
    boxShadow: '0 4px 12px rgba(94,114,228,0.35)',
  };
  const row = (isGlobal) => ({
    display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 20px',
    borderBottom: '1px solid #f0f2f5',
    background: isGlobal ? '#f8f9fe' : '#fff',
    transition: 'background .15s',
  });
  const badge = (color) => ({
    width: '34px', height: '34px', borderRadius: '10px',
    background: (color || '#8392ab') + '20', border: `2px solid ${color || '#8392ab'}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  });
  const iconBtn = (color) => ({
    background: color + '15', border: 'none', borderRadius: '8px',
    width: '32px', height: '32px', cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', color, fontSize: '13px',
  });
  const formPanel = {
    background: '#f8f9fe', borderTop: '1.5px solid #f0f2f5', padding: '24px 28px',
  };

  return (
    <div style={{ padding: '28px' }}>
      <div style={card}>
        {/* Header */}
        <div style={hdr}>
          <div>
            <p style={{ margin: 0, color: '#fff', fontWeight: '800', fontSize: '17px', letterSpacing: '-0.01em' }}>
              <i className="fas fa-layer-group" style={{ marginRight: '10px' }} />Pipeline Stage Manager
            </p>
            <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
              Manage global and custom stages for your pipeline
            </p>
          </div>
          <button style={addBtn} onClick={openCreate}>
            <i className="fas fa-plus" />Add Custom Stage
          </button>
        </div>

        {/* Add / Edit Form */}
        {showForm && (
          <div style={formPanel}>
            <p style={{ fontWeight: '700', color: '#344767', fontSize: '14px', marginBottom: '16px', marginTop: 0 }}>
              {editingId ? 'Edit Stage' : 'Create New Stage'}
            </p>
            {error && (
              <div style={{ background: '#fff5f5', border: '1px solid #f5365c', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', color: '#f5365c', fontSize: '13px' }}>
                <i className="fas fa-exclamation-circle" style={{ marginRight: '6px' }} />{error}
              </div>
            )}
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#8392ab', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Stage Name *</label>
                <input
                  ref={inputRef}
                  type="text" value={form.leadStatusName}
                  onChange={e => setForm(f => ({ ...f, leadStatusName: e.target.value }))}
                  placeholder="e.g. Property Visit Scheduled"
                  style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e9ecef', borderRadius: '10px', fontSize: '14px', color: '#344767', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: '0 0 120px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#8392ab', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Sort Order</label>
                <input
                  type="number" value={form.sortOrder} min={0}
                  onChange={e => setForm(f => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))}
                  style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e9ecef', borderRadius: '10px', fontSize: '14px', color: '#344767', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: '0 0 160px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#8392ab', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Color</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {PRESET_COLORS.map(c => (
                    <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                      style={{
                        width: '24px', height: '24px', borderRadius: '6px', background: c, border: 'none', cursor: 'pointer',
                        outline: form.color === c ? `2px solid ${c}` : 'none', outlineOffset: '2px',
                      }} title={c}
                    />
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '2px' }}>
                <button
                  onClick={cancel}
                  style={{ background: 'transparent', border: '1.5px solid #e9ecef', borderRadius: '10px', padding: '10px 18px', fontSize: '13px', fontWeight: '600', color: '#8392ab', cursor: 'pointer' }}
                >Cancel</button>
                <button
                  onClick={handleSave} disabled={saving}
                  style={{
                    background: 'linear-gradient(135deg,#5e72e4,#825ee4)', border: 'none',
                    borderRadius: '10px', padding: '10px 22px', fontSize: '13px', fontWeight: '700',
                    color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
                    display: 'flex', alignItems: 'center', gap: '6px',
                  }}
                >
                  <i className={saving ? 'fas fa-spinner fa-spin' : 'fas fa-save'} />
                  {saving ? 'Saving…' : 'Save Stage'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stage List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px 24px', color: '#8392ab' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }} />
            Loading stages…
          </div>
        ) : stages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px 24px' }}>
            <i className="fas fa-layer-group" style={{ fontSize: '36px', color: '#e9ecef', display: 'block', marginBottom: '12px' }} />
            <p style={{ color: '#8392ab', fontWeight: '600', margin: 0 }}>No stages found</p>
          </div>
        ) : (
          <div>
            {/* Column headers */}
            <div style={{ display: 'flex', gap: '14px', padding: '10px 20px', borderBottom: '2px solid #f0f2f5', background: '#f8f9fe' }}>
              <div style={{ width: '34px' }} />
              <p style={{ flex: 1, margin: 0, fontSize: '11px', fontWeight: '700', color: '#8392ab', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stage Name</p>
              <p style={{ width: '70px', margin: 0, fontSize: '11px', fontWeight: '700', color: '#8392ab', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>Order</p>
              <p style={{ width: '70px', margin: 0, fontSize: '11px', fontWeight: '700', color: '#8392ab', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>Type</p>
              <div style={{ width: '72px' }} />
            </div>

            {stages.map(stage => {
              const isGlobal = stage.clientId == null;
              const color = stage.color || '#8392ab';
              return (
                <div key={stage.id} style={row(isGlobal)}>
                  {/* Color dot */}
                  <div style={badge(color)}>
                    <i className="fas fa-grip-vertical" style={{ color, fontSize: '11px' }} />
                  </div>

                  {/* Name */}
                  <p style={{ flex: 1, margin: 0, fontWeight: '600', color: '#344767', fontSize: '14px' }}>
                    {stage.leadStatusName}
                  </p>

                  {/* Sort order */}
                  <p style={{ width: '70px', margin: 0, textAlign: 'center', color: '#8392ab', fontSize: '13px', fontWeight: '600' }}>
                    {stage.sortOrder || 0}
                  </p>

                  {/* Type badge */}
                  <div style={{ width: '70px', textAlign: 'center' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                      background: isGlobal ? '#e8f5e9' : '#e8f0ff',
                      color: isGlobal ? '#2dce89' : '#5e72e4',
                    }}>
                      {isGlobal ? 'Global' : 'Custom'}
                    </span>
                  </div>

                  {/* Actions */}
                  <div style={{ width: '72px', display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                    {!isGlobal && (
                      <>
                        <button style={iconBtn('#5e72e4')} onClick={() => openEdit(stage)} title="Edit">
                          <i className="fas fa-pencil-alt" />
                        </button>
                        <button
                          style={iconBtn('#f5365c')}
                          onClick={() => handleDelete(stage.id)}
                          disabled={deletingId === stage.id}
                          title="Delete"
                        >
                          <i className={deletingId === stage.id ? 'fas fa-spinner fa-spin' : 'fas fa-trash'} />
                        </button>
                      </>
                    )}
                    {isGlobal && (
                      <span title="Global stages are shared and cannot be edited here" style={{ fontSize: '12px', color: '#adb5bd' }}>
                        <i className="fas fa-lock" />
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
